import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { readGuestVacationLocation } from '@/lib/guestLocation';

type Center = { lat: number; lon: number; label: string; mode: 'vacation' | 'home' | 'geo' | 'default'; insee?: string | null };
const PARIS_DEFAULT: Center = { lat: 48.8566, lon: 2.3522, label: 'Paris', mode: 'default' };

export function useActiveCenter(): Center {
  const { profile } = useAuth();
  const [geoCenter, setGeoCenter] = useState<Center | null>(null);
  const [guestCenter, setGuestCenter] = useState<Center | null>(() => {
    const guestLocation = readGuestVacationLocation();
    if (!guestLocation?.active) return null;
    return {
      lat: guestLocation.lat,
      lon: guestLocation.lon,
      label: guestLocation.label,
      mode: 'vacation',
    };
  });

  useEffect(() => {
    const syncGuestCenter = () => {
      const guestLocation = readGuestVacationLocation();
      if (!guestLocation?.active) {
        setGuestCenter(null);
        return;
      }
      setGuestCenter({
        lat: guestLocation.lat,
        lon: guestLocation.lon,
        label: guestLocation.label,
        mode: 'vacation',
      });
    };

    window.addEventListener('storage', syncGuestCenter);
    window.addEventListener('lafete:guest-location-changed', syncGuestCenter);
    return () => {
      window.removeEventListener('storage', syncGuestCenter);
      window.removeEventListener('lafete:guest-location-changed', syncGuestCenter);
    };
  }, []);

  useEffect(() => {
    // Only try geolocation when profile has no location set
    const hasProfileLocation =
      (profile?.vacation_active && profile.vacation_lat != null) ||
      profile?.home_lat != null;
    if (hasProfileLocation || guestCenter || !navigator.geolocation) return;

    // Ne JAMAIS déclencher la demande de permission au démarrage : on n'utilise
    // la position que si l'accès a déjà été accordé. La demande explicite se
    // fait au moment du besoin (« Utiliser ma position », bouton de l'Explorer).
    let cancelled = false;
    (async () => {
      try {
        const status = await navigator.permissions?.query({ name: 'geolocation' as PermissionName });
        if (cancelled || status?.state !== 'granted') return;
        navigator.geolocation.getCurrentPosition(
          (pos) => { if (!cancelled) setGeoCenter({ lat: pos.coords.latitude, lon: pos.coords.longitude, label: 'Ma position', mode: 'geo' }); },
          () => {}, // silent fail → PARIS_DEFAULT used
          { timeout: 5000, maximumAge: 300_000 },
        );
      } catch { /* Permissions API absente → pas de géoloc implicite */ }
    })();
    return () => { cancelled = true; };
  }, [guestCenter, profile]);

  if (profile) {
    if (profile.vacation_active && profile.vacation_lat != null && profile.vacation_lon != null) {
      return { lat: profile.vacation_lat, lon: profile.vacation_lon, label: profile.vacation_label || 'Vacances', mode: 'vacation' };
    }
    if (profile.home_lat != null && profile.home_lon != null) {
      return { lat: profile.home_lat, lon: profile.home_lon, label: profile.home_city || 'Chez vous', insee: profile.home_insee, mode: 'home' };
    }
  }
  if (guestCenter) return guestCenter;
  return geoCenter ?? PARIS_DEFAULT;
}
